/**
 * Media Upload API
 *
 * Handles media file uploads for survey responses (audio, video, images).
 * Supports both API-proxied uploads and direct uploads via presigned URLs.
 */

import { api, apiRequest, getAccessToken } from './client'

// Use relative URL to go through Vite proxy in development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

/**
 * Supported media types and their MIME type prefixes
 * Note: Browsers may add codec parameters (e.g., 'audio/webm;codecs=opus')
 * so we match by prefix, not exact string
 */
export const SUPPORTED_MEDIA = {
  audio: ['audio/mpeg', 'audio/wav', 'audio/webm', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'],
  video: ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'],
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
}

/**
 * Normalize MIME type by removing codec parameters
 * e.g., 'audio/webm;codecs=opus' -> 'audio/webm'
 *
 * @param {string} mimeType - Full MIME type string
 * @returns {string} Base MIME type without parameters
 */
function normalizeMediaType(mimeType) {
  if (!mimeType) return ''
  // Split on semicolon and take the first part
  return mimeType.split(';')[0].trim().toLowerCase()
}

/**
 * Get the media type category from MIME type
 * Handles MIME types with codec parameters (e.g., 'audio/webm;codecs=opus')
 *
 * @param {string} mimeType - MIME type (e.g., 'audio/mpeg', 'audio/webm;codecs=opus')
 * @returns {'audio'|'video'|'image'|null} Media type category
 */
export function getMediaType(mimeType) {
  const normalized = normalizeMediaType(mimeType)
  for (const [type, mimes] of Object.entries(SUPPORTED_MEDIA)) {
    if (
      mimes.some((mime) => normalized === mime || normalized.startsWith(mime.split('/')[0] + '/'))
    ) {
      // Check if the normalized type matches any supported type
      if (mimes.includes(normalized)) {
        return type
      }
    }
  }
  // Fallback: check by media category prefix (audio/, video/, image/)
  if (normalized.startsWith('audio/')) return 'audio'
  if (normalized.startsWith('video/')) return 'video'
  if (normalized.startsWith('image/')) return 'image'
  return null
}

/**
 * Check if a MIME type is supported for upload
 * Handles MIME types with codec parameters
 *
 * @param {string} mimeType - MIME type to check
 * @returns {boolean} Whether the type is supported
 */
export function isSupportedMediaType(mimeType) {
  return getMediaType(mimeType) !== null
}

/**
 * Upload media file through the API server
 *
 * Use this for files under 10MB. For larger files, use getPresignedUrl().
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} [responseId] - Response UUID (optional, for uploads after response creation)
 * @param {string} questionName - Question name/ID the media is for
 * @param {File|Blob} file - File to upload
 * @param {Object} [options]
 * @param {function} [options.onProgress] - Progress callback (0-100)
 * @param {string} [options.uploadToken] - Upload token for anonymous uploads
 * @returns {Promise<Object>} Upload result with key, url, size_bytes, content_type
 */
export async function uploadMedia(surveyId, responseId, questionName, file, options = {}) {
  const { onProgress, uploadToken } = options

  const formData = new FormData()
  formData.append('question_name', questionName)
  formData.append('file', file, file.name || 'recording')

  // Include upload token for anonymous (unauthenticated) media uploads
  if (uploadToken) {
    formData.append('upload_token', uploadToken)
  }

  // Use endpoint with or without responseId based on availability
  // When responseId is null/undefined or "temp", use the survey-only endpoint
  const hasValidResponseId =
    responseId && responseId !== 'temp' && !responseId.toString().includes('temp')
  const uploadUrl = hasValidResponseId
    ? `${API_BASE_URL}/media/upload/${surveyId}/${responseId}`
    : `${API_BASE_URL}/media/upload/${surveyId}`

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          resolve(result)
        } catch {
          reject(new Error('Invalid response from server'))
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.detail || `Upload failed with status ${xhr.status}`))
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'))
    })

    xhr.open('POST', uploadUrl)

    // Add auth header if token exists
    const token = getAccessToken()
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    xhr.send(formData)
  })
}

/**
 * Get a presigned URL for direct upload to storage
 *
 * Use this for large files (>10MB) to bypass the API server.
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} questionName - Question name/ID
 * @param {string} filename - Original filename
 * @param {string} contentType - MIME type of the file
 * @returns {Promise<Object>} Presigned URL info with upload_url, key, expires_in
 */
export async function getPresignedUrl(surveyId, questionName, filename, contentType) {
  return api.post('/media/presigned-url', {
    survey_id: surveyId,
    question_name: questionName,
    filename,
    content_type: contentType,
  })
}

/**
 * Upload a file directly to storage using a presigned URL
 *
 * @param {string} presignedUrl - The presigned upload URL
 * @param {File|Blob} file - File to upload
 * @param {Object} [options]
 * @param {function} [options.onProgress] - Progress callback (0-100)
 * @returns {Promise<void>}
 */
export async function uploadDirect(presignedUrl, file, options = {}) {
  const { onProgress } = options

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Direct upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during direct upload'))
    })

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}

/**
 * Delete a media file from storage
 *
 * @param {string} key - Storage key/path of the file
 * @returns {Promise<void>}
 */
export async function deleteMedia(key) {
  return apiRequest(`/media/${encodeURIComponent(key)}`, { method: 'DELETE' })
}

/**
 * Upload a brand image for a survey
 *
 * @param {string} surveyId - Survey UUID
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} Upload result with key, url, size_bytes, content_type
 */
export async function uploadSurveyBrandImage(surveyId, file) {
  const formData = new FormData()
  formData.append('file', file, file.name || 'brand_image')

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Invalid response from server'))
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.detail || `Upload failed with status ${xhr.status}`))
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.open('POST', `${API_BASE_URL}/media/brand/survey/${surveyId}`)

    const token = getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.send(formData)
  })
}

/**
 * Upload a logo image for an organization
 *
 * @param {string} orgId - Organization UUID
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} Upload result with key, url, size_bytes, content_type
 */
export async function uploadOrganizationLogo(orgId, file) {
  const formData = new FormData()
  formData.append('file', file, file.name || 'org_logo')

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Invalid response from server'))
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.detail || `Upload failed with status ${xhr.status}`))
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.open('POST', `${API_BASE_URL}/media/brand/organization/${orgId}`)

    const token = getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.send(formData)
  })
}

/**
 * Create a media answer object for submission
 *
 * @param {Object} uploadResult - Result from uploadMedia()
 * @param {Object} [metadata]
 * @param {number} [metadata.durationMs] - Duration in milliseconds (for audio/video)
 * @param {number} [metadata.width] - Width in pixels (for video/image)
 * @param {number} [metadata.height] - Height in pixels (for video/image)
 * @returns {Object} Formatted media answer
 */
export function createMediaAnswer(uploadResult, metadata = {}) {
  const mediaType = getMediaType(uploadResult.content_type)

  const answer = {
    content_type: mediaType,
    media_url: uploadResult.url,
  }

  if (metadata.durationMs) {
    answer.media_duration_ms = metadata.durationMs
  }

  // Include additional metadata if provided
  if (metadata.width || metadata.height || metadata.durationMs) {
    answer.media_metadata = {}
    if (metadata.durationMs) answer.media_metadata.duration_ms = metadata.durationMs
    if (metadata.width) answer.media_metadata.width = metadata.width
    if (metadata.height) answer.media_metadata.height = metadata.height
    answer.media_metadata.size_bytes = uploadResult.size_bytes
    answer.media_metadata.format = uploadResult.content_type
  }

  return answer
}

/**
 * Upload media with automatic fallback to presigned URL for large files
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} [responseId] - Response UUID (optional, for pre-response uploads)
 * @param {string} questionName - Question name/ID
 * @param {File|Blob} file - File to upload
 * @param {Object} [options]
 * @param {function} [options.onProgress] - Progress callback (0-100)
 * @param {string} [options.uploadToken] - Upload token for anonymous uploads
 * @param {number} [options.directUploadThreshold=10485760] - Size threshold for direct upload (default 10MB)
 * @returns {Promise<Object>} Upload result
 */
export async function uploadMediaAuto(surveyId, responseId, questionName, file, options = {}) {
  const { onProgress, uploadToken, directUploadThreshold = 10 * 1024 * 1024 } = options

  // Use presigned URL for large files
  if (file.size > directUploadThreshold) {
    const presigned = await getPresignedUrl(
      surveyId,
      questionName,
      file.name || 'recording',
      file.type
    )
    await uploadDirect(presigned.upload_url, file, { onProgress })

    // Return similar structure to uploadMedia
    return {
      key: presigned.key,
      url: presigned.upload_url.split('?')[0], // URL without query params
      size_bytes: file.size,
      content_type: file.type,
    }
  }

  // Use API upload for smaller files (responseId is optional now)
  return uploadMedia(surveyId, responseId, questionName, file, { onProgress, uploadToken })
}

/**
 * Get file extension from MIME type
 *
 * @param {string} mimeType - MIME type
 * @returns {string} File extension (e.g., '.mp3')
 */
export function getExtensionFromMime(mimeType) {
  const extensions = {
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'audio/webm': '.webm',
    'audio/ogg': '.ogg',
    'audio/mp4': '.m4a',
    'audio/x-m4a': '.m4a',
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'video/quicktime': '.mov',
    'video/x-msvideo': '.avi',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
  }
  return extensions[mimeType] || ''
}

/**
 * Generate a filename for recorded media
 *
 * @param {string} questionName - Question name/ID
 * @param {string} mimeType - MIME type
 * @returns {string} Generated filename
 */
export function generateMediaFilename(questionName, mimeType) {
  const ext = getExtensionFromMime(mimeType)
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `${questionName}_${timestamp}${ext}`
}
